import { useCallback, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import { fetchTimeclockLocationPoint } from "../data/timeclock-location";
import { fetchTimeclockLog, TIMECLOCK_PUNCH_TYPES, TIMECLOCK_ROW_LIMIT, type TimeclockRow } from "../data/timeclock";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";
import type { PunchMapHighlight } from "../lib/punch-map-highlight";

// Timeclock report (read-only) — management_timeclock_log rendered as a punch list. Per the
// contract: report-only until the product timeclock workflow is designed — filters and labels,
// NO mutation controls. Redaction posture mirrors Live Activity: sha-redacted guard/device
// labels, no coordinates (location renders as derived status + accuracy label; denied/missing
// GPS is a field reality and renders muted, never as an error).
//
// The one coordinate affordance is Show on map (G3a): a NAVIGATION control, not a mutation, that
// fetches a single punch's point from the companion view on click and hands it to the Command
// map. The table itself still has no lat/lng cell and the report read stays coordinate-free.

/** Per-row state for the Show-on-map control. Only one row can be in flight at a time. */
type PunchLocationState =
  | { kind: "idle" }
  | { kind: "loading"; timePunchId: string }
  | { kind: "missing"; timePunchId: string }
  | { kind: "unavailable"; timePunchId: string };

function gpsDisplayLabel(row: TimeclockRow): string {
  if (row.locationStatus === "gps_captured") {
    return humanizeLabel(row.gpsAccuracyLabel ?? "gps_captured");
  }
  return humanizeLabel(row.locationStatus ?? "unknown");
}

/**
 * Only punches the log itself says captured GPS get the control. Offering it on a denied punch
 * would build a button whose only outcome is "no", which is the dead-looking control the brief
 * rules out. The derived status already tells that story in the GPS cell.
 */
function canHaveLocation(row: TimeclockRow): boolean {
  return row.locationStatus === "gps_captured";
}

/** Caption CAD shows over the highlight. Redacted guard ref only, same text as the table row. */
function punchHighlightLabel(row: TimeclockRow): string {
  return `${humanizeLabel(row.punchType)} · ${row.guardLabel}`;
}

export function TimeclockLog({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
  onShowPunchOnMap,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  /**
   * Hand a resolved punch point to the Command map. Omitted (e.g. an embedding without a map
   * route) means no Show-on-map column at all rather than a control that goes nowhere.
   */
  onShowPunchOnMap?: (highlight: PunchMapHighlight) => void;
}) {
  const { status, data, error } = useReadModel<TimeclockRow[]>(
    () => fetchTimeclockLog(client, accountId),
    [client, accountId],
    pollMs,
  );
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [locationState, setLocationState] = useState<PunchLocationState>({ kind: "idle" });

  const showOnMap = useCallback(
    async (row: TimeclockRow) => {
      if (onShowPunchOnMap === undefined) {
        return;
      }
      setLocationState({ kind: "loading", timePunchId: row.timePunchId });
      const lookup = await fetchTimeclockLocationPoint(client, accountId, row.timePunchId);
      if (lookup.status !== "found") {
        setLocationState({ kind: lookup.status, timePunchId: row.timePunchId });
        return;
      }
      setLocationState({ kind: "idle" });
      onShowPunchOnMap({
        timePunchId: lookup.point.timePunchId,
        lat: lookup.point.lat,
        lng: lookup.point.lng,
        accuracyMeters: lookup.point.gpsAccuracyMeters,
        label: punchHighlightLabel(row),
        occurredAt: row.serverReceivedAt ?? row.deviceTime,
        ...(row.routeAssignmentId == null ? {} : { routeAssignmentId: row.routeAssignmentId }),
        ...(row.siteId == null ? {} : { siteId: row.siteId }),
      });
    },
    [accountId, client, onShowPunchOnMap],
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading timeclock log…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load the timeclock log."}
      </p>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return <p className="surface__state" role="status">No time punches recorded yet.</p>;
  }

  const visible = typeFilter === "all" ? rows : rows.filter((row) => row.punchType === typeFilter);
  const capped = rows.length >= TIMECLOCK_ROW_LIMIT;
  const mapColumn = onShowPunchOnMap !== undefined;
  const toolbar = (
    <div className="timeclock__toolbar">
      <label className="timeclock__filter">
        <span>Punch type</span>
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          <option value="all">All types</option>
          {TIMECLOCK_PUNCH_TYPES.map((type) => (
            <option key={type} value={type}>
              {humanizeLabel(type)}
            </option>
          ))}
        </select>
      </label>
      <p className="timeclock__note" role="status">
        {capped ? `Showing the most recent ${rows.length} punches.` : `${rows.length} punches.`}
        {typeFilter !== "all" ? ` Filtered: ${visible.length} shown.` : ""}
      </p>
    </div>
  );

  if (visible.length === 0) {
    return (
      <div className="timeclock">
        {toolbar}
        <p className="surface__state" role="status">
          No {humanizeLabel(typeFilter).toLowerCase()} punches in the current report.
        </p>
      </div>
    );
  }

  return (
    <div className="timeclock">
      {toolbar}
      <table className="timeclock__table">
        <thead>
          <tr>
            <th scope="col">Guard</th>
            <th scope="col">Type</th>
            <th scope="col">Route · Site</th>
            <th scope="col">Punched (device)</th>
            <th scope="col">Received</th>
            <th scope="col">GPS</th>
            {mapColumn ? <th scope="col">Location</th> : null}
            <th scope="col">Flags</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr key={row.timePunchId}>
              <td>{row.guardLabel}</td>
              <td>
                <span className={`timeclock__chip timeclock__chip--${row.punchType}`}>
                  {humanizeLabel(row.punchType)}
                </span>
              </td>
              <td>
                {row.routeName ?? "—"}
                {row.siteName ? ` · ${row.siteName}` : ""}
              </td>
              <td>{row.deviceTime ? formatDisplayTime(row.deviceTime, timeZone) : "—"}</td>
              <td>{row.createdAt ? formatDisplayTime(row.createdAt, timeZone) : "—"}</td>
              <td className={row.locationStatus === "gps_captured" ? "" : "timeclock__muted"}>
                {gpsDisplayLabel(row)}
              </td>
              {mapColumn ? (
                <td className="timeclock__map-cell">
                  <PunchLocationCell
                    row={row}
                    state={locationState}
                    onShowOnMap={() => void showOnMap(row)}
                  />
                </td>
              ) : null}
              <td className="timeclock__flags">
                {row.offlineCreated ? <span className="timeclock__chip timeclock__chip--offline">offline</span> : null}
                {!row.routeAttached ? <span className="timeclock__chip timeclock__chip--adhoc">ad-hoc</span> : null}
                {row.notesPresent ? <span className="timeclock__chip">notes</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The location cell. A punch with no captured GPS gets an em dash, not a disabled button: the
 * GPS column beside it already says why, and a greyed control implies the location exists behind
 * a permission the operator might be able to get.
 */
function PunchLocationCell({
  row,
  state,
  onShowOnMap,
}: {
  row: TimeclockRow;
  state: PunchLocationState;
  onShowOnMap: () => void;
}) {
  if (!canHaveLocation(row)) {
    return <span className="timeclock__muted">—</span>;
  }
  const mine = state.kind !== "idle" && state.timePunchId === row.timePunchId;
  if (mine && state.kind === "missing") {
    return <span className="timeclock__muted">Location not available</span>;
  }
  if (mine && state.kind === "unavailable") {
    return <span className="timeclock__muted">Could not load location</span>;
  }
  return (
    <button
      type="button"
      className="timeclock__mapbtn"
      onClick={onShowOnMap}
      disabled={mine && state.kind === "loading"}
    >
      {mine && state.kind === "loading" ? "Opening…" : "Show on map"}
    </button>
  );
}
